import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheContext';
import axios from 'axios';
import { cachedGet } from '../utils/apiCache';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import AppLayout from '../components/AppLayout';
import { toast } from 'sonner';
import { trackSubscribe } from '../utils/tiktokPixel';
import { track, EVENTS } from '../utils/analytics';
import { 
  Sparkles, 
  Briefcase, 
  FolderOpen,
  TrendingUp,
  Clock,
  CheckCircle2,
  Award,
  X,
  Calendar,
  ChevronRight,
  Target,
  MapPin,
  Zap,
  Mail
} from 'lucide-react';
import OnboardingModal from '../components/OnboardingModal';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const StatCard = ({ icon, label, value, color, trend, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="bg-white dark:bg-[#1c1c1c] rounded-2xl border border-zinc-100 dark:border-white/[0.08] p-6 shadow-soft hover:shadow-hover transition-shadow duration-300 dark:shadow-[0_1px_20px_rgba(255,255,255,0.02)]"
  >
    <div className={`w-12 h-12 ${color} dark:bg-white/[0.07] rounded-xl flex items-center justify-center mb-4`}>
      {icon}
    </div>
    <p className="text-3xl font-heading font-bold text-zinc-900 dark:text-white mb-1">{value}</p>
    <div className="flex items-center justify-between">
      <p className="text-zinc-500 dark:text-zinc-500 font-medium">{label}</p>
      {trend && (
        <span className={`text-xs font-medium ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
  </motion.div>
);

const COLORS = ['#6366f1', '#14b8a6', '#f472b6', '#facc15', '#fb7185'];

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recentApplications, setRecentApplications] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [insights, setInsights] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { getAuthHeaders, user } = useAuth();
  const dataCache = useDataCache();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Restore cached data instantly for perceived speed
    const cached = dataCache?.get('dashboard');
    if (cached) {
      setStats(cached.stats);
      setAnalytics(cached.analytics);
      setRecentApplications(cached.recentApplications);
      setRecommendations(cached.recommendations);
      setInsights(cached.insights);
      setSubscription(cached.subscription);
      setLoading(false);
    }

    const sessionId = searchParams.get('session_id');
    const plan = searchParams.get('plan');
    const trialStarted = searchParams.get('trial_started');
    
    if (sessionId) {
      if (trialStarted) {
        pollTrialStatus(sessionId);
      } else if (plan) {
        pollPaymentStatus(sessionId, plan);
      }
    }
    
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user && !user.onboarding_completed) {
      setShowOnboarding(true);
    }
  }, [user]);

  const pollPaymentStatus = async (sessionId, plan, attempts = 0) => {
    if (attempts >= 5) return;
    
    try {
      const response = await axios.get(`${API}/subscription/checkout/status/${sessionId}`, {
        headers: getAuthHeaders()
      });
      
      if (response.data.payment_status === 'paid') {
        toast.success(`Successfully upgraded to ${plan.charAt(0).toUpperCase() + plan.slice(1)} plan!`);
        // TikTok Pixel — fire Purchase / Subscribe conversion (production only, consent-gated)
        try {
          const amount = response.data.amount_total ? response.data.amount_total / 100 : 0;
          const currency = (response.data.currency || 'EUR').toUpperCase();
          trackSubscribe({ value: amount, currency, plan, email: user?.email, userId: user?.id });
          track(EVENTS.CHECKOUT_COMPLETED, { plan, amount, currency });
          track(EVENTS.SUBSCRIPTION_UPGRADED, { plan });
        } catch (e) {}
        window.history.replaceState({}, '', '/dashboard');
        fetchData();
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, plan, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Payment status check failed:', error);
    }
  };

  const pollTrialStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 8) return;

    try {
      const response = await axios.get(`${API}/subscription/checkout/status/${sessionId}`, {
        headers: getAuthHeaders()
      });

      if (response.data.status === 'complete') {
        toast.success('Your 3-day free trial has started! Enjoy full access.');
        // TikTok Pixel — trial activation event (≈€5/mo recurring after 3 days)
        try {
          trackSubscribe({ value: 5, currency: 'EUR', plan: 'trial_basic', email: user?.email, userId: user?.id });
          track(EVENTS.TRIAL_STARTED, { plan: 'basic' });
          track(EVENTS.CHECKOUT_COMPLETED, { plan: 'trial_basic' });
        } catch (e) {}
        window.history.replaceState({}, '', '/dashboard');
        window.location.reload();
      } else {
        setTimeout(() => pollTrialStatus(sessionId, attempts + 1), 800);
      }
    } catch (error) {
      console.error('Trial status check failed:', error);
      // Keep retrying with a slight backoff on errors
      setTimeout(() => pollTrialStatus(sessionId, attempts + 1), 1500);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, analyticsRes, applicationsRes, recommendationsRes, insightsRes, subRes] = await Promise.all([
        cachedGet(`${API}/dashboard/stats`, { headers: getAuthHeaders() }, 60 * 1000),
        cachedGet(`${API}/analytics/dashboard`, { headers: getAuthHeaders() }, 60 * 1000),
        cachedGet(`${API}/applications/list`, { headers: getAuthHeaders() }, 30 * 1000),
        cachedGet(`${API}/recommendations/jobs?limit=6`, { headers: getAuthHeaders() }, 60 * 1000),
        cachedGet(`${API}/recommendations/insights`, { headers: getAuthHeaders() }, 5 * 60 * 1000).catch(() => ({ data: [] })),
        cachedGet(`${API}/subscription/current`, { headers: getAuthHeaders() }, 60 * 1000).catch(() => ({ data: null }))
      ]);
      const data = {
        stats: statsRes.data,
        analytics: analyticsRes.data,
        recentApplications: (applicationsRes.data || []).slice(0, 5),
        recommendations: recommendationsRes.data,
        insights: insightsRes.data,
        subscription: subRes.data,
      };
      setStats(data.stats);
      setAnalytics(data.analytics);
      setRecentApplications(data.recentApplications);
      setRecommendations(data.recommendations);
      setInsights(data.insights);
      setSubscription(data.subscription);
      dataCache?.set('dashboard', data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/10 dark:text-yellow-400',
    applied: 'bg-blue-100 text-blue-800 dark:bg-blue-500/10 dark:text-blue-400',
    interview: 'bg-teal-100 text-teal-800 dark:bg-teal-500/10 dark:text-teal-400',
    offer: 'bg-green-100 text-green-800 dark:bg-green-500/10 dark:text-green-400',
    rejected: 'bg-red-100 text-red-800 dark:bg-red-500/10 dark:text-red-400'
  };

  // Calculate today's stats from server
  const todayApps = stats?.today_applied || 0;
  const totalInterviews = stats?.interviews || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 dark:bg-[#121212] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const pieData = analytics?.by_source?.map((item, index) => ({
    name: item.source || 'Other',
    value: item.count
  })) || [];

  return (
    <AppLayout>
      {showOnboarding && (
        <OnboardingModal onComplete={() => setShowOnboarding(false)} />
      )}
      <div data-testid="dashboard-main">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <h1 className="font-heading text-3xl font-bold text-zinc-900 dark:text-white mb-2">
              Welcome back, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-zinc-600 dark:text-zinc-400">Here's an overview of your job search progress.</p>
          </motion.div>

          {/* Today's Progress Banner */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="bg-gradient-to-r from-indigo-500 to-indigo-600 dark:from-[#0a0a0a] dark:to-[#111] dark:border dark:border-white/[0.06] rounded-2xl p-5 mb-8 text-white shadow-lg shadow-indigo-500/20 dark:shadow-none"
            data-testid="todays-progress-banner">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 dark:bg-white/[0.06] dark:border dark:border-white/[0.08] rounded-xl flex items-center justify-center">
                  <Zap className="w-5 h-5 dark:text-cyan-400" />
                </div>
                <div>
                  <p className="font-heading font-semibold text-lg dark:text-white">Today's Progress</p>
                  <p className="text-indigo-100 dark:text-zinc-500 text-sm">Keep the momentum going!</p>
                </div>
              </div>
              <div className="flex items-center gap-6 sm:gap-8">
                <div className="text-center">
                  <p className="font-heading text-2xl sm:text-3xl font-bold dark:text-white" data-testid="today-applied-count">{todayApps}</p>
                  <p className="text-indigo-200 dark:text-zinc-500 text-xs font-medium">Applications Prepared Today</p>
                </div>
                <div className="w-px h-10 bg-white/20 dark:bg-white/[0.08]" />
                <div className="text-center">
                  <p className="font-heading text-2xl sm:text-3xl font-bold dark:text-white" data-testid="today-interviews-count">{totalInterviews}</p>
                  <p className="text-indigo-200 dark:text-zinc-500 text-xs font-medium">Interviews Scheduled</p>
                </div>
                {subscription && (
                  <>
                    <div className="w-px h-10 bg-white/20 dark:bg-white/[0.08]" />
                    <div className="text-center">
                      <p className="font-heading text-2xl sm:text-3xl font-bold dark:text-white" data-testid="tailorings-remaining-count">
                        {subscription.tailorings_limit === -1 ? '∞' : `${subscription.tailorings_remaining ?? 0}/${subscription.tailorings_limit ?? 0}`}
                      </p>
                      <p className="text-indigo-200 dark:text-zinc-500 text-xs font-medium">Tailorings Remaining{subscription.plan === 'trial' && subscription.trial_days_left != null ? ` · ${subscription.trial_days_left}d trial left` : ''}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>

          {/* Usage-Based Upgrade Trigger */}
          {subscription && subscription.usage_percentage >= 75 && subscription.plan !== 'unlimited' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              className={`rounded-2xl p-5 mb-8 border ${
                subscription.usage_percentage >= 90
                  ? 'bg-gradient-to-r from-red-50 to-amber-50 border-red-200 dark:from-[#1c1c1c] dark:to-[#1c1c1c] dark:border-red-500/20'
                  : 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200 dark:from-[#1c1c1c] dark:to-[#1c1c1c] dark:border-amber-500/20'
              }`}
              data-testid="upgrade-trigger-banner"
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    subscription.usage_percentage >= 90 ? 'bg-red-100 dark:bg-red-500/10' : 'bg-amber-100 dark:bg-amber-500/10'
                  }`}>
                    <Zap className={`w-5 h-5 ${subscription.usage_percentage >= 90 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
                  </div>
                  <div>
                    <p className={`font-heading font-semibold ${subscription.usage_percentage >= 90 ? 'text-red-800 dark:text-red-400' : 'text-amber-800 dark:text-amber-400'}`}>
                      {subscription.usage_label || `${subscription.usage_used}/${subscription.usage_limit} tailorings used`}
                    </p>
                    <p className={`text-sm ${subscription.usage_percentage >= 90 ? 'text-red-600 dark:text-red-500/70' : 'text-amber-600 dark:text-amber-500/70'}`}>
                      {subscription.usage_percentage >= 90
                        ? "You're almost at your limit — upgrade to keep applying!"
                        : "You're running low on applications this period."}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  {/* Usage bar */}
                  <div className="flex-1 sm:flex-initial sm:w-32 bg-white/80 dark:bg-white/[0.06] rounded-full h-2.5 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        subscription.usage_percentage >= 90 ? 'bg-red-500 dark:bg-red-500/80' : 'bg-amber-500 dark:bg-amber-500/80'
                      }`}
                      style={{ width: `${Math.min(subscription.usage_percentage, 100)}%` }}
                    />
                  </div>
                  <span className={`text-sm font-bold shrink-0 ${subscription.usage_percentage >= 90 ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>
                    {subscription.usage_percentage}%
                  </span>
                  <Button
                    onClick={() => navigate('/subscription')}
                    className={`rounded-xl text-white shrink-0 ${
                      subscription.usage_percentage >= 90
                        ? 'bg-red-600 hover:bg-red-700 dark:bg-white dark:text-black dark:hover:bg-zinc-200'
                        : 'bg-amber-600 hover:bg-amber-700 dark:bg-white dark:text-black dark:hover:bg-zinc-200'
                    }`}
                    data-testid="upgrade-trigger-btn"
                  >
                    Upgrade<span className="hidden sm:inline"> Now</span>
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
            <StatCard
              icon={<TrendingUp className="w-6 h-6 text-indigo-600" />}
              label="Applications Tracked"
              value={stats?.total_applications || 0}
              color="bg-indigo-100"
              delay={0.1}
            />
            <StatCard
              icon={<Clock className="w-6 h-6 text-yellow-600" />}
              label="Being Prepared"
              value={stats?.pending || 0}
              color="bg-yellow-100"
              delay={0.15}
            />
            <StatCard
              icon={<CheckCircle2 className="w-6 h-6 text-teal-600" />}
              label="Interviews"
              value={stats?.interviews || 0}
              color="bg-teal-100"
              delay={0.2}
            />
            <StatCard
              icon={<Award className="w-6 h-6 text-green-600" />}
              label="Offers"
              value={stats?.offers || 0}
              color="bg-green-100"
              delay={0.25}
            />
          </div>

          {/* Recommended Jobs for You - Primary Section */}
          {recommendations.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mb-8 bg-white dark:bg-[#1c1c1c] rounded-2xl border border-zinc-100 dark:border-white/[0.08] shadow-soft overflow-hidden"
              data-testid="recommended-jobs-section"
            >
              <div className="p-6 border-b border-zinc-100 dark:border-white/[0.08] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-semibold text-zinc-900 dark:text-white">Recommended for You</h2>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">Top jobs matching your resume skills</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/jobs')}
                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
                  data-testid="view-all-recommendations-btn"
                >
                  View All Jobs
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
                {recommendations.slice(0, 6).map((job, index) => (
                  <motion.div
                    key={job.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + (index * 0.05) }}
                    className="bg-zinc-50 dark:bg-white/[0.04] rounded-xl p-4 hover:bg-zinc-100 dark:hover:bg-white/[0.06] transition-colors cursor-pointer group border border-transparent hover:border-indigo-100 dark:hover:border-indigo-500/20"
                    onClick={() => navigate('/jobs')}
                    data-testid={`recommended-job-${index}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-zinc-900 dark:text-white text-sm line-clamp-1 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                          {job.job_title}
                        </h3>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{job.company}</p>
                      </div>
                      <div className={`ml-2 px-2.5 py-1 rounded-full text-xs font-bold ${
                        job.match_score >= 80 ? 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-400' :
                        job.match_score >= 60 ? 'bg-teal-100 text-teal-700 dark:bg-teal-500/10 dark:text-teal-400' :
                        job.match_score >= 40 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-400' :
                        'bg-zinc-200 text-zinc-600 dark:bg-white/[0.07] dark:text-zinc-400'
                      }`} data-testid={`recommended-score-${index}`}>
                        {job.match_score}% match
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2">
                      <MapPin className="w-3 h-3" />
                      <span className="line-clamp-1">{job.location}</span>
                    </div>
                    {job.matched_skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {job.matched_skills.slice(0, 3).map((skill, i) => (
                          <span key={i} className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 rounded text-xs capitalize">
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Applications Over Time */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-[#1c1c1c] rounded-2xl border border-zinc-100 dark:border-white/[0.08] p-6 shadow-soft"
            >
              <h3 className="font-heading text-lg font-semibold text-zinc-900 dark:text-white mb-4">Applications Over Time</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={analytics?.over_time || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#71717a' }} stroke="rgba(255,255,255,0.1)" />
                    <YAxis tick={{ fontSize: 12, fill: '#71717a' }} stroke="rgba(255,255,255,0.1)" />
                    <Tooltip contentStyle={{ backgroundColor: '#1c1c1c', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', color: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }} itemStyle={{ color: '#a5b4fc' }} labelStyle={{ color: '#a1a1aa' }} />
                    <Area type="monotone" dataKey="count" stroke="#818cf8" fill="rgba(99,102,241,0.15)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            {/* Applications by Source */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="bg-white dark:bg-[#1c1c1c] rounded-2xl border border-zinc-100 dark:border-white/[0.08] p-6 shadow-soft"
            >
              <h3 className="font-heading text-lg font-semibold text-zinc-900 dark:text-white mb-4">Applications by Source</h3>
              <div className="h-64 flex items-center justify-center">
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#1c1c1c', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', color: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }} itemStyle={{ color: '#a5b4fc' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-zinc-400 dark:text-zinc-500">No data yet</p>
                )}
              </div>
            </motion.div>
          </div>

          {/* Job Match Insights */}
          {insights.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="mb-8 bg-white dark:bg-[#1c1c1c] rounded-2xl border border-zinc-100 dark:border-white/[0.08] shadow-soft overflow-hidden"
              data-testid="job-match-insights"
            >
              <div className="p-6 border-b border-zinc-100 dark:border-white/[0.08] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-semibold text-zinc-900 dark:text-white">Job Match Insights</h2>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">Your top 5 best-matching opportunities right now</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/recommendations/send-daily-insights`, null, { headers: getAuthHeaders() });
                      toast.success('Daily insights email sent! Check your inbox.');
                    } catch (error) {
                      toast.error('Failed to send insights email');
                    }
                  }}
                  className="text-orange-600 border-orange-200 hover:bg-orange-50"
                  data-testid="send-daily-insights-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email Me This
                </Button>
              </div>
              <div className="divide-y divide-zinc-50 dark:divide-white/[0.04]">
                {insights.map((job, index) => {
                  const scoreColor = job.match_score >= 80 ? 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-500/10'
                    : job.match_score >= 60 ? 'text-teal-700 bg-teal-100 dark:text-teal-400 dark:bg-teal-500/10'
                    : job.match_score >= 40 ? 'text-yellow-700 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-500/10'
                    : 'text-zinc-600 bg-zinc-100 dark:text-zinc-400 dark:bg-white/5';
                  return (
                    <div key={job.id} className="flex items-center gap-4 px-6 py-4 hover:bg-zinc-50 dark:hover:bg-white/[0.05] transition-colors cursor-pointer group" onClick={() => navigate('/jobs')} data-testid={`insight-row-${index}`}>
                      <div className="w-8 h-8 rounded-lg bg-zinc-100 dark:bg-white/5 flex items-center justify-center text-zinc-500 dark:text-zinc-400 font-bold text-sm group-hover:bg-indigo-100 dark:group-hover:bg-indigo-500/10 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                        #{index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-zinc-900 dark:text-white text-sm group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors line-clamp-1">{job.job_title}</p>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400">{job.company} &bull; {job.location}</p>
                      </div>
                      <div className="hidden md:flex flex-wrap gap-1 max-w-xs">
                        {job.matched_skills?.slice(0, 3).map((skill, i) => (
                          <span key={i} className="px-2 py-0.5 bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-300 rounded text-xs capitalize">{skill}</span>
                        ))}
                        {job.missing_skills?.slice(0, 1).map((skill, i) => (
                          <span key={i} className="px-2 py-0.5 bg-red-50 dark:bg-red-500/15 text-red-500 dark:text-red-300 rounded text-xs capitalize">{skill}</span>
                        ))}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${scoreColor}`} data-testid={`insight-score-${index}`}>
                        {job.match_score}%
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-t border-zinc-100 flex items-center gap-2 text-sm text-zinc-600">
                <Mail className="w-4 h-4 text-orange-500" />
                <span>Pro users get daily match insights emails at 8 AM automatically</span>
              </div>
            </motion.div>
          )}

          {/* Bottom Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Applications */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="lg:col-span-2 bg-white dark:bg-[#1c1c1c] rounded-2xl border border-zinc-100 dark:border-white/[0.08] shadow-soft overflow-hidden"
            >
              <div className="p-6 border-b border-zinc-100 dark:border-white/[0.08] flex items-center justify-between">
                <h2 className="font-heading text-xl font-semibold text-zinc-900 dark:text-white">Recent Applications</h2>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/applications')}
                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
                  data-testid="view-all-applications-btn"
                >
                  View All
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <div className="divide-y divide-zinc-100 dark:divide-white/[0.06]">
                {recentApplications.length > 0 ? (
                  recentApplications.map((app) => (
                    <div key={app.id} className="p-4 hover:bg-zinc-50 dark:hover:bg-white/[0.05] transition-colors">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-zinc-900 dark:text-white">{app.job_title}</p>
                          <p className="text-sm text-zinc-500 dark:text-zinc-400">{app.company}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${statusColors[app.status]}`}>
                          {app.status}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-zinc-500 dark:text-zinc-400">
                    <FolderOpen className="w-12 h-12 mx-auto mb-3 text-zinc-300 dark:text-zinc-600" />
                    <p>No applications yet</p>
                    <Button 
                      onClick={() => navigate('/jobs')}
                      className="mt-4 bg-indigo-500 hover:bg-indigo-600 text-white rounded-full"
                      data-testid="browse-jobs-btn"
                    >
                      Browse Jobs
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="space-y-6"
            >
              {/* Jobs Available */}
              <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-6 text-white">
                <Briefcase className="w-10 h-10 mb-4 opacity-80" />
                <p className="text-4xl font-heading font-bold mb-1">{stats?.jobs_available || 0}</p>
                <p className="text-white/80 mb-4">Jobs Available</p>
                <Button 
                  onClick={() => navigate('/jobs')}
                  className="bg-white text-indigo-600 hover:bg-zinc-100 rounded-full w-full"
                  data-testid="view-jobs-btn"
                >
                  Browse Jobs
                </Button>
              </div>

              {/* Upcoming Interviews */}
              <div className="bg-white dark:bg-[#1c1c1c] rounded-2xl border border-zinc-100 dark:border-white/[0.08] p-6 shadow-soft">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-6 h-6 text-teal-500" />
                  <h3 className="font-heading font-semibold text-zinc-900 dark:text-white">Upcoming Interviews</h3>
                </div>
                {analytics?.upcoming_interviews?.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.upcoming_interviews.slice(0, 2).map((interview) => (
                      <div key={interview.id} className="p-3 bg-zinc-50 dark:bg-white/[0.04] rounded-xl">
                        <p className="font-medium text-zinc-900 dark:text-white text-sm">{interview.job_title}</p>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400">{interview.company}</p>
                        <p className="text-xs text-teal-600 dark:text-teal-400 mt-1">{new Date(interview.scheduled_at).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">No upcoming interviews</p>
                )}
              </div>
            </motion.div>
          </div>

      </div>
    </AppLayout>
  );
};

export default Dashboard;
